import { useEffect, useRef, useState } from 'react';
import { useBenefitMigration } from './useBenefitMigration';
function canRetrySubmission(read) {
    return !read || ['acceptance-required', 'address-required'].includes(read.snapshot.phase);
}
export function useSubmitBenefitMigration() {
    const migration = useBenefitMigration();
    const busy = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [needsReconciliation, setNeedsReconciliation] = useState(false);
    const scopeKey = JSON.stringify(migration.scope);
    useEffect(() => {
        busy.current = null;
        setIsSubmitting(false);
        setError(null);
        setErrorCode(null);
        setNeedsReconciliation(false);
    }, [scopeKey]);
    const submit = async (input) => {
        const { scope, service, isCurrent, refetch } = migration;
        if (busy.current || !scope || !service)
            return;
        const token = Symbol();
        busy.current = token;
        setIsSubmitting(true);
        setError(null);
        setErrorCode(null);
        const recordReconciliation = (current) => {
            if (!isCurrent(scope))
                return;
            setNeedsReconciliation(!current);
            if (canRetrySubmission(current))
                setError('submission-failed');
        };
        const resumeReconciledSubmission = async () => {
            const current = await refetch();
            if (!isCurrent(scope))
                return false;
            if (!current) {
                setError('submission-failed');
                return false;
            }
            setNeedsReconciliation(false);
            return canRetrySubmission(current);
        };
        try {
            if (needsReconciliation && !(await resumeReconciledSubmission()))
                return;
            const response = await service.submit(scope, input);
            if (!isCurrent(scope))
                return;
            if (response.kind === 'error')
                setErrorCode(response.code ?? null);
            if (response.kind === 'error' && response.reason === 'invalid-address') {
                setError('invalid-address');
                return;
            }
            recordReconciliation(await refetch());
        }
        catch {
            if (isCurrent(scope))
                recordReconciliation(await refetch().catch(() => null));
        }
        finally {
            if (busy.current === token) {
                busy.current = null;
                if (isCurrent(scope))
                    setIsSubmitting(false);
            }
        }
    };
    return { submit, isSubmitting, error, errorCode };
}
